import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { settingsApi } from '../../api/settings_api';
import { ApiError } from '../../api/utils';
import { useAuthStore } from '../../stores/root_store';

// Opened from the "Confirm your email address" email: /verify-email/:token
// (POST /users/email/add in the backend). Works signed in or out.
export const VerifyEmailPage = observer(() => {
  const { token } = useParams<{ token: string }>();
  const authStore = useAuthStore();
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // the link is single use, so don't verify twice (StrictMode remounts)
    if (started.current) return;
    started.current = true;
    settingsApi
      .verifyEmail(token ?? '')
      .then((r) => setEmail(r.email))
      .catch((err) => {
        // the API's 404 body is plain text, so give it words here
        if (err instanceof ApiError && err.status === 404) {
          setError("This link isn't valid. It may have been used already.");
        } else {
          setError(err instanceof Error ? err.message : 'Unable to confirm this address');
        }
      });
  }, [token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 440, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Confirm Email
          </Typography>

          {!email && !error && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {email && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {email} has been added. Requests emailed from it will now show up on this account.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {(email || error) && (
            <Button
              component={RouterLink}
              to={authStore.isAuthenticated ? '/settings/edit' : '/login'}
              fullWidth
              variant="contained"
              size="large"
            >
              {authStore.isAuthenticated ? 'Back to Settings' : 'Sign In'}
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
});
